import "./style.scss";
import { FC, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import LogoSvg from "@/src/svg/default/LogoSvg";
import BtnDefault from "../../../../BtnDefault/BtnDefault";
import {
  AOPMaltaPaymentConfirmationData,
  AOPPolandPaymentConfirmationData,
  AOPUkrainePaymentConfirmationData,
} from "@/src/types/admissionOffice";
import { Editor } from "@tinymce/tinymce-react";
import { splitHtmlByBlocks, splitHtmlByBlocksWithFontConvert } from "../../../converterPtToRem";
import AutocompleteInput from "@/src/components/AutocompleteInput/AutocompleteInput";
import { useAutocomplete } from "@/src/hooks/useAutocomplete";
import { useAdmissionTaskPrefill } from "@/src/hooks/useAdmissionTaskPrefill";
import { syncCourseAndNationalityAutocompletes } from "@/src/constants/AdmissionOffice/admissionTaskPrefillMappings";
import { admissionOfficeRequests } from "@/src/api/admissionOfficeDocCreateRequests";
import { PAYMENT_CONFIRMATION_BASIC_REGISTRY } from "../paymentConfirmationBasicRegistry";
import type { AdmissionOfficeCountry } from "../../../admissionOfficeCountry";
import { Nationalities } from "@/src/constants/globalConsts";
import { admissionOfficeWhoseDoc } from "@/src/constants/AdmissionOffice/admissionsOfficeConsts";
import { AODSvgExport } from "@/src/svg/admissionOfficePage/AOPSvgExport";
import { useDispatch } from "react-redux";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import { useAdmissionGeneratedDocumentActions } from "@/src/hooks/useAdmissionGeneratedDocumentActions";
import AOPGeneratedDocumentToolbar from "@/src/components/AdmissionsOfficePage/AOPGeneratedDocumentToolbar";

interface IAOPFormValues {
  whose_doc: string;
  currentDate: string;
  fullName: string;
  dateOfBirth: string;
  nationality: string;
  course: string;
  selectedCourse: string;
  mediumOfInstruction: string;
  duration: string;
  paymentConfirmationBody: string;
  signature: string;
  coursePrefix: string;
  task_id?: string;
}

interface AOPConfigProps {
  country: AdmissionOfficeCountry;
  dataForGeneration: AOPMaltaPaymentConfirmationData;
  setDataForGeneration: React.Dispatch<React.SetStateAction<AOPMaltaPaymentConfirmationData>>;
  setBodyForPrev: React.Dispatch<React.SetStateAction<AOPMaltaPaymentConfirmationData>>;
  setIsPrevReady: React.Dispatch<boolean>;
}

const today = new Date();

const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const AOPConfig: FC<AOPConfigProps> = ({ country, dataForGeneration, setDataForGeneration, setBodyForPrev, setIsPrevReady }) => {
  const registryEntry = PAYMENT_CONFIRMATION_BASIC_REGISTRY[country];
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm<IAOPFormValues>({
    defaultValues: {
      paymentConfirmationBody: "",
      currentDate: formatDateForInput(today),
    },
  });

  const dispatch = useDispatch();
  const [content, setContent] = useState<string>("");
  const [paymentConfirmationContentHeader, setPaymentConfirmationContentHeader] = useState<string>("");
  const [paymentConfirmationContentFooter, setPaymentConfirmationContentFooter] = useState<string>("");

  const [title, setTitle] = useState<string>("");
  const [isPreviewReady, setIsPreviewReady] = useState<boolean>(false);
  const generatedDoc = useAdmissionGeneratedDocumentActions({ getValues });

  const course = watch("course");
  const paymentConfirmationBody = watch("paymentConfirmationBody");
  // AUTOCOMPLETE ↓
  const signatureAuto = useAutocomplete(Object.keys(registryEntry.signatures));
  const offeredCourseAuto = useAutocomplete(Object.keys(registryEntry.admissionCourses));
  const paymentConfirmationBodyAuto = useAutocomplete(Object.keys(registryEntry.paymentCourses));
  const nationalitiesAuto = useAutocomplete(Nationalities);
  const whoseDocAuto = useAutocomplete(admissionOfficeWhoseDoc);

  useAdmissionTaskPrefill<IAOPFormValues>({
    setValue,
    country,
    documentSlug: "payment",
    onPrefill: (payload, mapping, formBaseline) => {
      generatedDoc.setTaskPrefillSnapshot(payload, mapping, formBaseline);
      syncCourseAndNationalityAutocompletes(payload, offeredCourseAuto, nationalitiesAuto, whoseDocAuto);
    },
  });

  // EDITORS
  useEffect(() => {
    if (!course) {
      setContent("");
      return;
    }
    const courseSettings = PAYMENT_CONFIRMATION_BASIC_REGISTRY[country].admissionCourses[course];
    if (courseSettings) {
      setContent(courseSettings.tinyMCEContent);
      setValue("selectedCourse", courseSettings.selected_course);
      setValue("mediumOfInstruction", courseSettings.medium_of_instruction);
      setValue("duration", courseSettings.duration);
      setTitle(courseSettings.title);
    } else {
      setContent("");
    }
  }, [course, setValue, country]);

  useEffect(() => {
    if (!paymentConfirmationBody) {
      setPaymentConfirmationContentHeader("");
      setPaymentConfirmationContentFooter("");
      return;
    }
    const paymentSettings = PAYMENT_CONFIRMATION_BASIC_REGISTRY[country].paymentCourses[paymentConfirmationBody];
    setPaymentConfirmationContentHeader(paymentSettings ? paymentSettings.tinyMCEContentHeader : "");
    setPaymentConfirmationContentFooter(paymentSettings ? paymentSettings.tinyMCEContentFooter : "");
  }, [paymentConfirmationBody, country]);

  const generateNewDocument = async () => {
    generatedDoc.resetGeneratedState();
    dispatch(setIsPreloyder(true));
    try {
      let response;
      if (country === "malta") {
        response = await admissionOfficeRequests.createPaymentConfirmationDoc(dataForGeneration);
      } else if (country === "poland") {
        response = await admissionOfficeRequests.createPolandPaymentConfirmationDoc(dataForGeneration as AOPPolandPaymentConfirmationData);
      } else {
        response = await admissionOfficeRequests.createUkrainePaymentConfirmationDoc(dataForGeneration as AOPUkrainePaymentConfirmationData);
      }
      generatedDoc.onDocumentGenerated(response);
    } catch (error) {
      alert("An unexpected error occurred - write to the developers");
    } finally {
      dispatch(setIsPreloyder(false));
    }
    setIsPreviewReady(false);
  };

  const handlePreview = () => {
    const watchedFields = {
      coursePrefix: watch("coursePrefix"),
      selectedCourse: watch("selectedCourse"),
      duration: watch("duration"),
      fullName: watch("fullName"),
      mediumOfInstruction: watch("mediumOfInstruction"),
      nationality: watch("nationality"),
      title: title,
      whose_doc: watch("whose_doc"),
      currentDate: watch("currentDate"),
      dateOfBirth: watch("dateOfBirth"),
    };

    setDataForGeneration((prev) => ({
      ...prev,
      currentDate: watchedFields.currentDate ? parseDate(watchedFields.currentDate) : "",
      fullName: watchedFields.fullName || "",
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      nationality: watchedFields.nationality || "",
      course: watchedFields.selectedCourse || "",
      whose_doc: watchedFields.whose_doc || "",
      title: watchedFields.title || "",
      duration: watchedFields.duration || "",
      mediumOfInstruction: watchedFields.mediumOfInstruction || "",
      coursePrefix: watchedFields.coursePrefix || "",
      payment_header: splitHtmlByBlocks(paymentConfirmationContentHeader),
      payment_footer: splitHtmlByBlocks(paymentConfirmationContentFooter),
    }));

    setBodyForPrev((prev) => ({
      ...prev,
      currentDate: watchedFields.currentDate ? parseDate(watchedFields.currentDate) : "",
      fullName: watchedFields.fullName || "",
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      nationality: watchedFields.nationality || "",
      course: watchedFields.selectedCourse || "",
      whose_doc: watchedFields.whose_doc || "",
      title: watchedFields.title || "",
      duration: watchedFields.duration || "",
      mediumOfInstruction: watchedFields.mediumOfInstruction || "",
      coursePrefix: watchedFields.coursePrefix || "",
      payment_header: splitHtmlByBlocksWithFontConvert(paymentConfirmationContentHeader),
      payment_footer: splitHtmlByBlocksWithFontConvert(paymentConfirmationContentFooter),
    }));

    const hasContent = content.trim().length > 0 || paymentConfirmationContentHeader.trim().length > 0;
    if (hasContent) {
      setIsPrevReady(true);
      setIsPreviewReady(true);
      generatedDoc.resetGeneratedState();
    } else {
      setIsPrevReady(false);
    }
  };

  const handlePaymentConfirmationEditorChangeHeader = (newPaymentConfirmationContentHeader: string) =>
    setPaymentConfirmationContentHeader(newPaymentConfirmationContentHeader);

  const handlePaymentConfirmationEditorChangeFooter = (newPaymentConfirmationContentFooter: string) =>
    setPaymentConfirmationContentFooter(newPaymentConfirmationContentFooter);

  function parseDate(value: string) {
    const [year, month, day] = value.split("-");
    return `${day}.${month}.${year}`;
  }

  const handleCopy = async (fieldName: string) => {
    const value = getValues(fieldName as any);
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
    } catch (err) {
      console.error("Ошибка копирования", err);
    }
  };

  return (
    <>
      <div className="admission-office-page-config">
        <div className="admission-office-page-config-logo">
          <div className="logo-svg">
            <LogoSvg />
          </div>
          <div className="logo-line"></div>
          <p>Platforma</p>
        </div>
        <p className="admission-office-page-config-title">Payment confirmation letter</p>
        <form className="admission-office-page-config-form">
          <div className="form-group-fields">
            <div className="form-group-fields-container">
              <div className="form-group-fields-input whose_doc">
                <AutocompleteInput
                  placeholder="Whose"
                  value={whoseDocAuto.query}
                  onChange={whoseDocAuto.onChange}
                  onSelect={(v) => {
                    whoseDocAuto.onSelect(v);
                    setValue("whose_doc", v);
                  }}
                  isOpen={whoseDocAuto.isOpen}
                  options={whoseDocAuto.filtered}
                  error={!!errors.paymentConfirmationBody}
                  required
                  inputProps={{
                    register: register("whose_doc", { required: true }),
                    close: whoseDocAuto.close,
                    setOpen: whoseDocAuto.onClick,
                  }}
                  identifier={"whose"}
                />
              </div>
              <div className="form-group-fields-input coursePrefix">
                <input className="coursePrefix" id="coursePrefix" type="text" {...register("coursePrefix")} />
              </div>
            </div>
            <div className="form-group-fields-input currentDate">
              <div className="form-group-fields-input-label">
                <label htmlFor="currentDate">
                  Date of Issue<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="currentDate" id="currentDate" type="date" {...register("currentDate", { required: true })} />
              {errors.currentDate && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="full-name">
                  Full Name<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="full-mame" id="full-mame" type="text" {...register("fullName", { required: true })} />
              {errors.fullName && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <div className="form-group-fields-input-svg-line"></div>
                <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("fullName")}>
                  <AODSvgExport.FullNameCopySvg />
                </div>
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="dateOfBirth">
                  Data of Birth<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="dateOfBirth" id="dateOfBirth" type="date" {...register("dateOfBirth", { required: true })} />
              {errors.dateOfBirth && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
            <AutocompleteInput
              label="Nationality"
              value={nationalitiesAuto.query}
              onChange={nationalitiesAuto.onChange}
              onSelect={(v) => {
                nationalitiesAuto.onSelect(v);
                setValue("nationality", v);
              }}
              isOpen={nationalitiesAuto.isOpen}
              options={nationalitiesAuto.filtered}
              error={!!errors.nationality}
              required
              inputProps={{
                register: register("nationality", { required: true }),
                close: nationalitiesAuto.close,
                setOpen: nationalitiesAuto.onClick,
              }}
              identifier={"nationality"}
            />
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input course">
              <AutocompleteInput
                label="Select the offered course"
                value={offeredCourseAuto.query}
                onChange={offeredCourseAuto.onChange}
                onSelect={(v) => {
                  offeredCourseAuto.onSelect(v);
                  setValue("course", v);
                }}
                isOpen={offeredCourseAuto.isOpen}
                options={offeredCourseAuto.filtered}
                error={!!errors.course}
                required
                inputProps={{
                  register: register("course", { required: true }),
                  close: offeredCourseAuto.close,
                  setOpen: offeredCourseAuto.onClick,
                  setValue,
                  clearErrors,
                  name: "course",
                }}
                identifier={"offered-course-auto"}
              />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="selected-сourse">
                  Selected course<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="selected-сourse" id="selected-сourse" type="text" {...register("selectedCourse", { required: true })} />
              {errors.selectedCourse && <p>This field is required</p>}
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="medium-of-instruction">
                  Medium of Instruction<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input
                className="medium-of-instruction"
                id="medium-of-instruction"
                type="text"
                {...register("mediumOfInstruction", { required: true })}
              />
              {errors.mediumOfInstruction && <p>This field is required</p>}
            </div>
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="duration">
                  Duration<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="duration" id="duration" type="text" {...register("duration", { required: true })} />
              {errors.duration && <p>This field is required</p>}
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input invoiceBody">
              <AutocompleteInput
                label="Select a sample"
                value={paymentConfirmationBodyAuto.query}
                onChange={paymentConfirmationBodyAuto.onChange}
                onSelect={(v) => {
                  paymentConfirmationBodyAuto.onSelect(v);
                  setValue("paymentConfirmationBody", v);
                }}
                isOpen={paymentConfirmationBodyAuto.isOpen}
                options={paymentConfirmationBodyAuto.filtered}
                error={!!errors.paymentConfirmationBody}
                required
                inputProps={{
                  register: register("paymentConfirmationBody", { required: true }),
                  close: paymentConfirmationBodyAuto.close,
                  setOpen: paymentConfirmationBodyAuto.onClick,
                }}
                identifier={"payment"}
              />
            </div>
          </div>
          <label>Payment confirmation body (header)</label>
          <Editor
            value={paymentConfirmationContentHeader}
            init={{
              height: 300,
              menubar: false,
              plugins: ["lists", "link", "code", "table"],
              toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
              branding: false,
              toolbar_mode: "wrap",
              licenseKey: "gpl",
              license_key: "gpl" as any,
              content_style: `
                body { font-family: Arial, sans-serif; padding: 1rem; margin: 0; }
                p { margin: 0; }
              `,
            }}
            onEditorChange={handlePaymentConfirmationEditorChangeHeader}
            tinymceScriptSrc="/tinymce/tinymce.min.js"
          />
          <label>Payment confirmation body (footer)</label>
          <Editor
            value={paymentConfirmationContentFooter}
            init={{
              height: 300,
              menubar: false,
              plugins: ["lists", "link", "code", "table"],
              toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
              branding: false,
              toolbar_mode: "wrap",
              licenseKey: "gpl",
              license_key: "gpl" as any,
              content_style: `
                body { font-family: Arial, sans-serif; padding: 1rem; margin: 0; }
                p { margin: 0; }
              `,
            }}
            onEditorChange={handlePaymentConfirmationEditorChangeFooter}
            tinymceScriptSrc="/tinymce/tinymce.min.js"
          />
          <AOPGeneratedDocumentToolbar
            isGenerated={generatedDoc.isGenerated}
            generatedPdfUrl={generatedDoc.generatedPdfUrl}
            isGeneratedPdfPreviewOpen={generatedDoc.isGeneratedPdfPreviewOpen}
            setIsGeneratedPdfPreviewOpen={generatedDoc.setIsGeneratedPdfPreviewOpen}
            generatedPreviewTitle={generatedDoc.generatedPreviewTitle}
            onDownload={generatedDoc.handleDownloadGeneratedPdf}
            onApprove={generatedDoc.handleApproveGeneratedDocument}
            onReject={generatedDoc.handleRejectGeneratedDocument}
            previewButton={
              <BtnDefault onClick={handlePreview} reverse={true}>
                <p>Preview doc</p>
                <AODSvgExport.PreviewSvg />
              </BtnDefault>
            }
            generateButton={
              <BtnDefault onClick={handleSubmit(generateNewDocument)} disabled={!isPreviewReady}>
                <p>Generate doc</p>
                <AODSvgExport.GenerateSvg />
              </BtnDefault>
            }
          />
          <div className="form-group-fields">
            <div className="form-group-fields-input signature">
              <AutocompleteInput
                label="Signature"
                value={signatureAuto.query}
                defaultValue="Fedorova Anzhelika"
                onChange={signatureAuto.onChange}
                onSelect={(v) => {
                  signatureAuto.onSelect(v);
                  setValue("signature", v);
                }}
                isOpen={signatureAuto.isOpen}
                options={signatureAuto.filtered}
                error={!!errors.signature}
                required
                inputProps={{
                  register: register("signature", { required: true }),
                  close: signatureAuto.close,
                  setOpen: signatureAuto.onClick,
                }}
                identifier={"signature"}
              />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="task_id">Task Id</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="task_id" id="task_id" type="text" {...register("task_id")} />
              <div className="form-group-fields-input-svg">
                <div className="form-group-fields-input-svg-line"></div>
                <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("task_id")}>
                  <AODSvgExport.FullNameCopySvg />
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default AOPConfig;
